/**
 * System Templates Tab
 * Displays system templates organized by category
 *
 * @file src/components/email/templates/SystemTemplatesTab.tsx
 * @created 2025-10-22
 */

import React, { useEffect, useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Alert, AlertDescription } from '@/components/ui/alert';
import TemplateGrid from './TemplateGrid';
import TemplateCustomizationModal from './TemplateCustomizationModal';
import EmailService, { EmailTemplate } from '@/api/email';
import { Loader2, Sparkles, Mail, Newspaper, Tag, AlertCircle } from 'lucide-react';

interface SystemTemplatesTabProps {
  organisationId: string;
  restaurantId?: string;
  onTemplateSelect: (designJson: Record<string, any>, templateId: string) => void;
}

const CATEGORIES = [
  {
    value: 'promotional',
    label: 'Promotional',
    icon: Tag,
    description: 'Sales, offers, and special promotions'
  },
  {
    value: 'newsletter',
    label: 'Newsletter',
    icon: Newspaper,
    description: 'Regular updates, news, and announcements'
  },
  {
    value: 'transactional',
    label: 'Transactional',
    icon: Mail,
    description: 'Order confirmations, receipts, and notifications'
  },
  {
    value: 'trigger',
    label: 'Automated',
    icon: Sparkles,
    description: 'Welcome emails, win-back campaigns, and birthday messages'
  }
];

/**
 * SystemTemplatesTab Component
 *
 * Features:
 * - Fetches system templates from API
 * - Organizes by category (Promotional, Newsletter, Transactional, Trigger)
 * - Shows template count per category
 * - Opens customization modal when template is selected
 * - Passes customized design_json back to parent
 *
 * @example
 * <SystemTemplatesTab
 *   organisationId="org-123"
 *   restaurantId="rest-456"
 *   onTemplateSelect={(designJson, templateId) => {
 *     editor.loadProjectData(designJson);
 *   }}
 * />
 */
const SystemTemplatesTab: React.FC<SystemTemplatesTabProps> = ({
  organisationId,
  restaurantId,
  onTemplateSelect
}) => {
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedTemplate, setSelectedTemplate] = useState<EmailTemplate | null>(null);
  const [showCustomization, setShowCustomization] = useState(false);

  useEffect(() => {
    fetchSystemTemplates();
  }, [organisationId]);

  const fetchSystemTemplates = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch system templates only
      const response = await EmailService.fetchTemplates(organisationId, {
        // Note: Backend needs to support is_system_template filter
        // For now, we'll fetch all and filter client-side
      });

      // Filter for system templates only
      const systemTemplates = response.filter(t => t.is_system_template);

      setTemplates(systemTemplates);
    } catch (error: any) {
      console.error('Error fetching system templates:', error);
      setError(error.message || 'Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  const handleTemplateClick = (template: EmailTemplate) => {
    setSelectedTemplate(template);
    setShowCustomization(true);
  };

  const handleCustomizationComplete = (designJson: Record<string, any>) => {
    if (selectedTemplate) {
      onTemplateSelect(designJson, selectedTemplate.id);
    }
    setShowCustomization(false);
    setSelectedTemplate(null);
  };

  const getCategoryIcon = (categoryValue: string) => {
    const category = CATEGORIES.find(c => c.value === categoryValue);
    return category ? category.icon : Mail;
  };

  const getCategoryLabel = (categoryValue: string) => {
    const category = CATEGORIES.find(c => c.value === categoryValue);
    return category?.label || categoryValue;
  };

  const getCategoryDescription = (categoryValue: string) => {
    const category = CATEGORIES.find(c => c.value === categoryValue);
    return category?.description || '';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-primary mx-auto mb-4" />
          <p className="text-gray-600">Loading templates...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>
          {error}
        </AlertDescription>
      </Alert>
    );
  }

  if (templates.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <Sparkles className="h-16 w-16 text-gray-400 mb-4" />
        <p className="text-gray-600 font-medium mb-2">No System Templates Available</p>
        <p className="text-sm text-gray-500">
          System templates will be added soon. In the meantime, you can create your own templates.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {CATEGORIES.map(category => {
        const categoryTemplates = templates.filter(t => t.category === category.value);

        if (categoryTemplates.length === 0) return null;

        const Icon = category.icon;

        return (
          <div key={category.value}>
            <div className="flex items-center gap-3 mb-4">
              <Icon className="h-5 w-5 text-primary" />
              <div className="flex-1">
                <h3 className="font-semibold text-lg">{category.label}</h3>
                <p className="text-sm text-gray-600">{category.description}</p>
              </div>
              <Badge variant="secondary" className="ml-auto">
                {categoryTemplates.length}{' '}
                {categoryTemplates.length === 1 ? 'template' : 'templates'}
              </Badge>
            </div>

            <TemplateGrid
              templates={categoryTemplates}
              onTemplateClick={handleTemplateClick}
              showCustomizeButton={true}
            />

            <Separator className="mt-8" />
          </div>
        );
      })}

      {/* Customization Modal */}
      {selectedTemplate && (
        <TemplateCustomizationModal
          open={showCustomization}
          onClose={() => {
            setShowCustomization(false);
            setSelectedTemplate(null);
          }}
          template={selectedTemplate}
          organisationId={organisationId}
          restaurantId={restaurantId}
          onComplete={handleCustomizationComplete}
        />
      )}
    </div>
  );
};

export default SystemTemplatesTab;
